import { useRef, useState } from "react";
import { Upload, X, Plus, Images, Ruler, Palette, Star, Camera } from "lucide-react";
import { toast } from "sonner";

export const readResized = (file, max = 900) => new Promise((resolve) => {
  const reader = new FileReader();
  reader.onload = () => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, max / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL("image/jpeg", 0.82));
    };
    img.src = reader.result;
  };
  reader.readAsDataURL(file);
});

const SIZE_PRESETS = ["XS", "S", "M", "L", "XL", "2XL", "3XL"];

function TagInput({ icon: Icon, label, values, onChange, presets = [], placeholder, testid, renderChipExtra }) {
  const [draft, setDraft] = useState("");
  const add = (raw) => {
    const items = String(raw).split(/[,\n]/).map((s) => s.trim()).filter(Boolean);
    if (!items.length) return;
    onChange([...new Set([...values, ...items])]);
    setDraft("");
  };
  return (
    <div data-testid={testid}>
      <label className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5"><Icon className="w-3.5 h-3.5 text-pb-red" /> {label}</label>
      <div className="flex flex-wrap gap-1 mb-1.5 min-h-[22px]">
        {values.map((v) => (
          <span key={v} className="inline-flex items-center gap-1 bg-pb-elevated border border-pb-border px-2 py-0.5 text-xs" data-testid={`${testid}-chip-${v.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            {renderChipExtra?.(v)}
            {v}<button type="button" onClick={() => onChange(values.filter((x) => x !== v))} className="text-zinc-500 hover:text-red-400"><X className="w-3 h-3" /></button>
          </span>
        ))}
        {!values.length && <span className="text-[10px] text-zinc-600">None — item sold as-is</span>}
      </div>
      <div className="flex gap-1.5">
        <input data-testid={`${testid}-input`} value={draft} placeholder={placeholder} onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(draft); } }}
          className="flex-1 bg-pb-elevated border border-pb-border px-2.5 py-1.5 text-xs text-white placeholder-zinc-600 focus:outline-none focus:border-pb-red" />
        <button type="button" data-testid={`${testid}-add`} onClick={() => add(draft)} className="border border-pb-border hover:border-pb-red px-2.5 text-xs"><Plus className="w-3.5 h-3.5" /></button>
      </div>
      {presets.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1.5">
          {presets.filter((p) => !values.includes(p)).map((p) => (
            <button key={p} type="button" data-testid={`${testid}-preset-${p.toLowerCase()}`} onClick={() => add(p)} className="text-[9px] uppercase tracking-widest border border-dashed border-zinc-700 text-zinc-500 hover:text-white hover:border-pb-red px-1.5 py-0.5">+{p}</button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ProductMediaEditor({ value, onChange, testid = "pme" }) {
  const fileRef = useRef(null);
  const [url, setUrl] = useState("");
  const [swatchFor, setSwatchFor] = useState(null);
  const images = value.images || [];
  const colorPhotos = value.color_photos || {};
  const set = (patch) => onChange({ ...value, ...patch });
  const setImages = (imgs) => set({ images: imgs, image: imgs[0] || "", color_photos: Object.fromEntries(Object.entries(colorPhotos).filter(([, src]) => imgs.includes(src))) });
  const setColors = (colors) => set({ colors, color_photos: Object.fromEntries(Object.entries(colorPhotos).filter(([c]) => colors.includes(c))) });
  const setSwatch = (color, src) => { set({ color_photos: src ? { ...colorPhotos, [color]: src } : Object.fromEntries(Object.entries(colorPhotos).filter(([c]) => c !== color)) }); setSwatchFor(null); };
  const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const added = await Promise.all(files.map((f) => readResized(f)));
    setImages([...images, ...added]);
    toast.success(`${added.length} photo${added.length === 1 ? "" : "s"} attached`);
    e.target.value = "";
  };
  const addUrl = () => { const u = url.trim(); if (!u) return; setImages([...images, u]); setUrl(""); };

  return (
    <div className="space-y-4" data-testid={testid}>
      <div>
        <label className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5"><Images className="w-3.5 h-3.5 text-pb-red" /> Product Photos ({images.length}) — first photo is the cover</label>
        <div className="flex flex-wrap gap-2 mb-2">
          {images.map((src, i) => (
            <div key={i} className="relative group/ph" data-testid={`${testid}-photo-${i}`}>
              <img src={src} alt="" className={`w-16 h-16 object-cover border ${i === 0 ? "border-pb-red" : "border-pb-border"}`} />
              {i === 0 && <span className="absolute top-0 left-0 bg-pb-red text-black text-[8px] font-black px-1 flex items-center gap-0.5"><Star className="w-2 h-2" />COVER</span>}
              <div className="absolute inset-x-0 bottom-0 flex opacity-0 group-hover/ph:opacity-100 transition-opacity">
                {i > 0 && <button type="button" title="Make cover" data-testid={`${testid}-photo-${i}-cover`} onClick={() => setImages([src, ...images.filter((_, j) => j !== i)])} className="flex-1 bg-black/80 text-pb-red text-[8px] font-black uppercase py-0.5">Cover</button>}
                <button type="button" title="Remove" data-testid={`${testid}-photo-${i}-remove`} onClick={() => setImages(images.filter((_, j) => j !== i))} className="flex-1 bg-black/80 text-red-400 text-[8px] font-black uppercase py-0.5">✕</button>
              </div>
            </div>
          ))}
          <button type="button" data-testid={`${testid}-upload-btn`} onClick={() => fileRef.current?.click()}
            className="w-16 h-16 border border-dashed border-pb-border hover:border-pb-red flex flex-col items-center justify-center gap-1 text-zinc-500 hover:text-white transition-colors">
            <Upload className="w-4 h-4" /><span className="text-[8px] uppercase tracking-widest">Add</span>
          </button>
          <input ref={fileRef} type="file" accept="image/*" multiple onChange={onFiles} className="hidden" data-testid={`${testid}-file-input`} />
        </div>
        <div className="flex gap-1.5">
          <input data-testid={`${testid}-url`} value={url} placeholder="…or paste an image URL" onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addUrl(); } }}
            className="flex-1 bg-pb-elevated border border-pb-border px-2.5 py-1.5 text-xs text-white placeholder-zinc-600 focus:outline-none focus:border-pb-red" />
          <button type="button" data-testid={`${testid}-url-add`} onClick={addUrl} className="border border-pb-border hover:border-pb-red px-2.5 text-xs"><Plus className="w-3.5 h-3.5" /></button>
        </div>
        <p className="text-[10px] text-zinc-600 mt-1">Shoppers swipe through these on the store card and tap to enlarge with the full description.</p>
      </div>
      <TagInput icon={Ruler} label="Sizes offered" values={value.sizes || []} onChange={(sizes) => set({ sizes })} presets={SIZE_PRESETS} placeholder="e.g. 7.8oz, Youth, 4.25in grip" testid={`${testid}-sizes`} />
      <TagInput icon={Palette} label="Colors offered — tap the camera on a color to link its photo" values={value.colors || []} onChange={setColors} placeholder="e.g. Lime, Black, Cyan (comma separated)" testid={`${testid}-colors`}
        renderChipExtra={(c) => (
          <button type="button" title={colorPhotos[c] ? "Change linked photo" : "Link a photo to this color"} data-testid={`${testid}-swatch-btn-${slug(c)}`}
            onClick={() => setSwatchFor(swatchFor === c ? null : c)}
            className={`inline-flex items-center justify-center w-5 h-5 border ${colorPhotos[c] ? "border-pb-red" : "border-dashed border-zinc-600 text-zinc-500 hover:text-white"}`}>
            {colorPhotos[c] ? <img src={colorPhotos[c]} alt="" className="w-full h-full object-cover" /> : <Camera className="w-3 h-3" />}
          </button>
        )} />
      {swatchFor && (
        <div className="border border-pb-red/40 bg-black/40 p-3" data-testid={`${testid}-swatch-picker`}>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 mb-2">Photo for <span className="text-white font-bold">{swatchFor}</span> — shoppers jump to it when they pick this color</div>
          {images.length === 0 ? <div className="text-xs text-zinc-600">Add product photos above first.</div> : (
            <div className="flex flex-wrap gap-2">
              {images.map((src, i) => (
                <button key={i} type="button" data-testid={`${testid}-swatch-pick-${i}`} onClick={() => setSwatch(swatchFor, src)}
                  className={`w-14 h-14 border-2 transition-colors ${colorPhotos[swatchFor] === src ? "border-pb-red" : "border-transparent hover:border-zinc-500"}`}>
                  <img src={src} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
              {colorPhotos[swatchFor] && <button type="button" data-testid={`${testid}-swatch-clear`} onClick={() => setSwatch(swatchFor, null)} className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-red-400 self-center px-2">Unlink</button>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
